// frontend/src/askos/MySubjectsPage.js
//
// Lists the student's accessible subjects; clicking one drills into two
// separate sections: Studio Content (unit-wise, via the units endpoint) and
// Faculty Shared Resources (a flat, course-wide list of TeachingMaterial
// faculty have explicitly shared — not unit-scoped, and deliberately kept
// out of the Studio content reader so it doesn't get buried as that feed
// grows term over term). Clicking a unit opens ContentReaderPage; clicking
// a resource opens FacultyResourceReaderPage.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { askosAuthHeader } from './askosApi';
import { searchLectureNotes } from '../api/liveSessionsApi';

const MATERIAL_TYPE_LABELS = {
    lecture_notes: 'Lecture Notes',
    case_study: 'Case Study',
    slide_deck: 'Slide Deck',
    document: 'Document',
};

const MySubjectsPage = ({ slug }) => {
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeSubject, setActiveSubject] = useState(null);
    const [units, setUnits] = useState([]);
    const [loadingUnits, setLoadingUnits] = useState(false);
    const [resources, setResources] = useState([]);
    const [loadingResources, setLoadingResources] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        api.get(`/tenant/${slug}/askos/projects`, { headers: askosAuthHeader() })
            .then(res => setSubjects(res.data || []))
            .catch(() => setSubjects([]))
            .finally(() => setLoading(false));
    }, [slug]);

    const openSubject = (subject) => {
        setActiveSubject(subject);
        setSearchQuery('');
        setSearchResults(null);
        setLoadingUnits(true);
        setLoadingResources(true);
        api.get(`/tenant/${slug}/askos/projects/${subject.id}/units`, { headers: askosAuthHeader() })
            .then(res => setUnits(res.data || []))
            .catch(() => setUnits([]))
            .finally(() => setLoadingUnits(false));
        api.get(`/tenant/${slug}/askos/projects/${subject.id}/resources`, { headers: askosAuthHeader() })
            .then(res => setResources(res.data || []))
            .catch(() => setResources([]))
            .finally(() => setLoadingResources(false));
    };

    const runSearch = async (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;
        setSearching(true);
        try {
            const results = await searchLectureNotes(slug, searchQuery.trim(), activeSubject.id);
            setSearchResults(results);
        } catch (e2) {
            setSearchResults([]);
        } finally {
            setSearching(false);
        }
    };

    if (activeSubject) {
        return (
            <div className="subpage">
                <button type="button" className="secondary-btn" style={{ marginBottom: 16 }} onClick={() => setActiveSubject(null)}>
                    ← Back to Subjects
                </button>
                <h2>{activeSubject.name}</h2>

                <h4 style={{ marginTop: 20, marginBottom: 10 }}>Search Lecture Notes</h4>
                <form onSubmit={runSearch} className="d-flex gap-2 mb-2" style={{ maxWidth: 480 }}>
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Search keywords from a lecture..."
                        style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd' }}
                    />
                    <button type="submit" disabled={searching} className="primary-btn">Search</button>
                </form>
                {searchResults !== null && (
                    <div style={{ marginBottom: 24 }}>
                        {searchResults.length === 0 && <p className="empty-note">No matching lecture notes found.</p>}
                        {searchResults.map(r => (
                            <div key={r.chunk_id} className="resource-card" style={{ marginBottom: 8 }}>
                                <strong>{r.topic}</strong>
                                <span style={{ display: 'block', fontSize: 13, color: '#666', whiteSpace: 'pre-wrap' }}>{r.excerpt}</span>
                            </div>
                        ))}
                    </div>
                )}

                <h4 style={{ marginTop: 20, marginBottom: 10 }}>Studio Content</h4>
                {loadingUnits && <p className="empty-note">Loading…</p>}
                {!loadingUnits && units.length === 0 && (
                    <p className="empty-note">No content has been published for this subject yet — check back once your faculty/SME finalizes a unit.</p>
                )}
                <div className="subjects-grid-full">
                    {units.map(u => (
                        <div key={u.id} className="resource-card" style={{ cursor: 'pointer' }} onClick={() => navigate(`/samkalp-ias/reader/${activeSubject.id}/${u.unit_id ?? u.id}`)}>
                            <strong>{u.name}</strong>
                            {(u.topics || []).length > 0 && (
                                <span>Covers: {u.topics.map(t => t.name).join(', ')}</span>
                            )}
                            <em>{(u.topics || []).length} concept{(u.topics || []).length === 1 ? '' : 's'} covered</em>
                        </div>
                    ))}
                </div>

                <h4 style={{ marginTop: 28, marginBottom: 10 }}>Faculty Shared Resources</h4>
                {loadingResources && <p className="empty-note">Loading…</p>}
                {!loadingResources && resources.length === 0 && (
                    <p className="empty-note">No resources shared yet — check back once your faculty shares lecture notes, slides, or other material.</p>
                )}
                <div className="subjects-grid-full">
                    {resources.map(r => (
                        <div key={r.id} className="resource-card" style={{ cursor: 'pointer' }} onClick={() => navigate(`/samkalp-ias/resources/${activeSubject.id}/${r.id}`)}>
                            <strong>{r.title}</strong>
                            <span>{MATERIAL_TYPE_LABELS[r.material_type] || r.material_type}{r.concept_name ? ` · ${r.concept_name}` : ''}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="subpage">
            <h2>My Subjects</h2>
            {loading && <p className="empty-note">Loading…</p>}
            {!loading && subjects.length === 0 && <p className="empty-note">No subjects available.</p>}
            <div className="subjects-grid-full">
                {subjects.map(s => (
                    <div key={s.id} className="resource-card" style={{ cursor: 'pointer' }} onClick={() => openSubject(s)}>
                        <strong>{s.name}</strong>
                        <em>{s.status}</em>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default MySubjectsPage;
